// Tableau de bord — parcours clair : comprendre -> créer -> suivre
import {
  Box,
  Flex,
  Grid,
  SimpleGrid,
  Text,
  Button,
  Badge,
  Icon,
  Image,
  useColorModeValue,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import {
  FaPlus,
  FaArrowRight,
  FaChevronRight,
  FaBolt,
} from "react-icons/fa";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import LineChart from "components/Charts/LineChart";
import React, { useEffect, useState } from "react";
import { statut } from "variables/veille";
import { lineChartOptions } from "variables/charts";
import { useVeilles } from "contexts/VeillesContext";
import { useSession } from "contexts/SessionContext";
import { supabase } from "lib/supabase";
import martin from "assets/img/martin.png";

const ETAPES = [
  { titre: "Vous décrivez le sujet", desc: "Le sujet ou la marque à surveiller." },
  { titre: "Notre agent IA Martin", desc: "Il cadre la veille : secteur, mots-clés, canaux." },
  { titre: "Vous recevez les rapports", desc: "Au format et au rythme voulus." },
];

const CANAL_COURT = { presse: "Presse", radio: "Radio", tele: "TV", reseaux: "Réseaux", web: "Web", juridique: "Juridique" };

export default function Dashboard() {
  const textColor = useColorModeValue("gray.700", "white");
  const history = useHistory();
  const { veilles } = useVeilles();
  const { profile } = useSession();
  // Salutation : on prend le prénom (1er mot du nom complet). Si on n'a qu'un e-mail, on l'utilise proprement.
  const nomComplet = ((profile && profile.full_name) || "").trim();
  const prenom = nomComplet && !nomComplet.includes("@")
    ? nomComplet.split(/\s+/)[0]
    : (((profile && profile.email) || "").split("@")[0] || "vous");

  const aucuneVeille = veilles.length === 0;
  // Un compte neuf n'a aucune donnée d'activité : on masque les graphiques/stats fictifs.
  const aucuneActivite = !veilles.some((v) => (v.mentions || 0) > 0);

  // Série réelle « tonalité dans le temps » (agrégat quotidien du moteur), si des mentions existent
  const [serieReelle, setSerieReelle] = useState(null);
  useEffect(() => {
    let actif = true;
    supabase
      .from("veille_daily_stats")
      .select("jour, positif, negatif")
      .order("jour")
      .then(({ data }) => {
        if (actif && data && data.length) {
          setSerieReelle({
            categories: data.map((d) => new Date(d.jour).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })),
            series: [
              { name: "Positives", data: data.map((d) => d.positif) },
              { name: "Négatives", data: data.map((d) => d.negatif) },
            ],
          });
        }
      });
    return () => { actif = false; };
  }, []);

  const ordre = { active: 0, programmee: 1, en_pause: 2, terminee: 3, brouillon: 4 };
  const mesVeilles = [...veilles].sort((a, b) => (ordre[a.statut] ?? 99) - (ordre[b.statut] ?? 99)).slice(0, 5);
  // Alertes flash : derniers déclenchements RÉELS (table alert_triggers, filtrée par la RLS).
  const [alertesActives, setAlertesActives] = useState([]);
  useEffect(() => {
    let actif = true;
    supabase
      .from("alert_triggers")
      .select("id, valeur, triggered_at, alerts(nom)")
      .order("triggered_at", { ascending: false })
      .limit(3)
      .then(({ data }) => { if (actif) setAlertesActives(data || []); });
    return () => { actif = false; };
  }, []);

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      {/* En-tête de bienvenue */}
      <Flex align="center" mb="24px">
        <Box w="7px" h="60px" borderRadius="full" bg="teal.300" me="18px" flexShrink={0} />
        <Box>
          <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} lineHeight="1.1">Bonjour {prenom}</Text>
          <Text fontSize="md" color="gray.700" mt="4px">Voici l'état de vos veilles média aujourd'hui.</Text>
        </Box>
      </Flex>

      {/* Bandeau Martin — présentation de l'agent + frise du parcours */}
      <Card
        mb="24px"
        position="relative"
        bgImage="linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)"
        boxShadow="0 16px 34px rgba(49,56,96,0.32)"
      >
        {/* CTA — coin haut droit */}
        <Button
          position="absolute"
          top="20px"
          right="24px"
          zIndex={2}
          bg="teal.300"
          color="white"
          _hover={{ bg: "teal.400" }}
          borderRadius="12px"
          leftIcon={<Icon as={FaPlus} />}
          onClick={() => history.push("/admin/nouvelle-veille")}
        >
          Créer une veille
        </Button>
        <CardBody>
          <Flex align="center" direction={{ base: "column", lg: "row" }} gap={{ base: "24px", lg: "44px" }} w="100%" py={{ base: "6px", lg: "12px" }}>
            {/* Martin */}
            <Box position="relative" flexShrink={0} w={{ base: "150px", md: "168px" }} h={{ base: "150px", md: "168px" }}>
              <Box position="absolute" top="50%" left="50%" transform="translate(-50%,-50%)" w="150px" h="150px" borderRadius="full" bg="radial-gradient(circle, rgba(79,209,197,0.45) 0%, rgba(79,209,197,0) 70%)" filter="blur(6px)" />
              <Image src={martin} alt="Martin, agent de veille" position="relative" h="100%" mx="auto" filter="drop-shadow(0 12px 22px rgba(0,0,0,0.45))" />
            </Box>

            {/* Frise horizontale du parcours */}
            <Box flex="1" w={{ base: "100%", lg: "auto" }} pt="4px">
              <Text color="teal.300" fontSize="sm" fontWeight="bold" textTransform="uppercase" letterSpacing="1.5px" mb="20px">Comment ça marche</Text>
              <Box position="relative">
                <Box display={{ base: "none", md: "block" }} position="absolute" left="23px" right="23px" top="23px" h="3px" borderRadius="full" bgGradient="linear(to-r, teal.300, whiteAlpha.300)" />
                <SimpleGrid columns={{ base: 1, md: 3 }} spacingX="18px" spacingY="22px" position="relative">
                  {ETAPES.map((e, i) => (
                    <Flex key={i} direction={{ base: "row", md: "column" }} align={{ base: "center", md: "flex-start" }} gap={{ base: "16px", md: "0" }}>
                      <Flex w="46px" h="46px" borderRadius="full" bg="teal.300" color="white" align="center" justify="center" fontSize="xl" fontWeight="bold" flexShrink={0} border="4px solid" borderColor="#1a1f36" zIndex={1}>
                        {i + 1}
                      </Flex>
                      <Box mt={{ md: "16px" }}>
                        <Text color="white" fontSize="lg" fontWeight="bold" lineHeight="1.2">{e.titre}</Text>
                        <Text color="gray.300" fontSize="sm" mt="4px">{e.desc}</Text>
                      </Box>
                    </Flex>
                  ))}
                </SimpleGrid>
              </Box>
            </Box>
          </Flex>
        </CardBody>
      </Card>

      {/* Vos veilles — section grise + cartes blanches */}
      <Box bg="gray.100" borderRadius="20px" p={{ base: "16px", md: "22px" }}>
        <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap="12px" mb="16px" px="4px">
          <Flex align="center">
            <Box w="6px" h="46px" borderRadius="full" bg="teal.300" me="16px" flexShrink={0} />
            <Box>
              <Text fontSize="lg" color={textColor} fontWeight="bold">Vos veilles</Text>
              <Text fontSize="sm" color="gray.700">Ce que Martin surveille pour vous en ce moment.</Text>
            </Box>
          </Flex>
          <Button size="sm" bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="10px" rightIcon={<Icon as={FaArrowRight} />} onClick={() => history.push("/admin/veilles")}>Tout voir</Button>
        </Flex>

        {aucuneVeille ? (
          <Flex direction="column" align="center" textAlign="center" py="30px" px="16px">
            <Text fontSize="md" fontWeight="bold" color={textColor}>Vous n'avez pas encore de veille</Text>
            <Text fontSize="sm" color="gray.600" mt="4px" maxW="420px">Décrivez votre sujet, Martin cadre la veille et vous livrera des rapports clairs.</Text>
            <Button mt="16px" bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" leftIcon={<Icon as={FaPlus} />} onClick={() => history.push("/admin/nouvelle-veille")}>Créer ma première veille</Button>
          </Flex>
        ) : (
        <Flex direction="column" gap="12px">
          {mesVeilles.map((v) => {
            const actif = v.mentions > 0;
            return (
              <Flex
                key={v.id}
                align="center"
                bg="white"
                borderRadius="14px"
                px="18px"
                py="16px"
                cursor="pointer"
                boxShadow="0 2px 6px rgba(20,20,43,0.04)"
                transition="all .15s"
                _hover={{ transform: "translateY(-3px)", boxShadow: "0 14px 28px rgba(20,20,43,0.12)" }}
                onClick={() => history.push(`/admin/veilles/${v.id}`)}
              >
                <Box w="4px" h="42px" borderRadius="full" bg="gray.300" me="16px" flexShrink={0} />
                <Box flex="1" minW="0" me="20px">
                  <Flex align="center" gap="10px" mb="3px">
                    <Text fontSize="md" fontWeight="bold" color={textColor} noOfLines={1}>{v.nom}</Text>
                    <Badge colorScheme={statut(v.statut).color} borderRadius="6px" flexShrink={0}>{statut(v.statut).label}</Badge>
                  </Flex>
                  <Text fontSize="sm" color="gray.500" noOfLines={1}>{v.secteur} · {v.canaux.map((c) => CANAL_COURT[c] || c).join(", ")}</Text>
                </Box>
                <Flex align="center" gap={{ md: "40px" }} flexShrink={0} display={{ base: "none", md: "flex" }}>
                  <Box textAlign="right">
                    <Text fontSize="xs" color="gray.500" mb="3px">Prochain rapport</Text>
                    <Text fontSize="md" fontWeight="bold" color={textColor}>{v.prochaineDate}</Text>
                  </Box>
                  <Box textAlign="right" minW="72px">
                    <Text fontSize="xs" color="gray.500" mb="3px">Retombées</Text>
                    <Text fontSize="2xl" fontWeight="bold" lineHeight="1" color={actif ? textColor : "gray.400"}>{actif ? v.mentions : "—"}</Text>
                  </Box>
                  <Icon as={FaChevronRight} color="gray.300" ml="4px" />
                </Flex>
              </Flex>
            );
          })}
        </Flex>
        )}
      </Box>

      {/* Bas : alertes flash (gauche) + graphique (droite) — masqué tant qu'il n'y a aucune activité */}
      {!aucuneActivite && (
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1.5fr" }} gap="24px" mt="24px">
        {/* Alertes flash */}
        <Card>
          <CardHeader mb="14px">
            <Flex justify="space-between" align="center" w="100%">
              <Box>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>Alertes flash</Text>
                <Text fontSize="sm" color="gray.700">Les signaux à ne pas manquer.</Text>
              </Box>
              <Button size="sm" bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="10px" onClick={() => history.push("/admin/alertes")}>Gérer</Button>
            </Flex>
          </CardHeader>
          <CardBody>
            <Flex direction="column" w="100%" gap="10px">
              {alertesActives.length === 0 && (
                <Flex direction="column" align="center" textAlign="center" py="20px" px="8px">
                  <Text fontSize="sm" fontWeight="bold" color="gray.700">Aucune alerte déclenchée</Text>
                  <Text fontSize="xs" color="gray.500" mt="4px">Configurez des règles pour être prévenu des signaux importants.</Text>
                </Flex>
              )}
              {alertesActives.map((a) => (
                <Flex key={a.id} align="center" p="12px" borderRadius="12px" bg="gray.50">
                  <Flex w="38px" h="38px" borderRadius="10px" bg="red.50" align="center" justify="center" me="12px" flexShrink={0}>
                    <Icon as={FaBolt} color="red.400" />
                  </Flex>
                  <Box minW="0" flex="1">
                    <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>{(a.alerts && a.alerts.nom) || "Alerte"}</Text>
                    <Text fontSize="xs" color="gray.600" noOfLines={1}>{a.triggered_at ? new Date(a.triggered_at).toLocaleString("fr-FR") : ""}</Text>
                  </Box>
                  {a.valeur > 0 && (
                    <Badge colorScheme="red" borderRadius="6px" ml="8px">{a.valeur}</Badge>
                  )}
                </Flex>
              ))}
            </Flex>
          </CardBody>
        </Card>

        {/* Graphique — tonalité dans le temps */}
        <Card>
          <CardHeader mb="20px">
            <Box>
              <Text fontSize="lg" fontWeight="bold" color={textColor}>Tonalité dans le temps</Text>
              <Text fontSize="sm" color="gray.700">{serieReelle ? "Mentions positives et négatives par jour" : "En attente de données"}</Text>
            </Box>
          </CardHeader>
          <CardBody h="220px">
            <Box w="100%" h="100%">
              {serieReelle ? (
                <LineChart
                  series={serieReelle.series}
                  options={{
                    ...lineChartOptions,
                    xaxis: { ...lineChartOptions.xaxis, categories: serieReelle.categories },
                    colors: ["#4FD1C5", "#F56565"],
                    fill: { ...lineChartOptions.fill, colors: ["#4FD1C5", "#F56565"] },
                  }}
                />
              ) : (
                <Flex align="center" justify="center" h="100%"><Text fontSize="sm" color="gray.400">Pas encore assez de données pour tracer la courbe.</Text></Flex>
              )}
            </Box>
          </CardBody>
        </Card>
      </Grid>
      )}
    </Flex>
  );
}
